import { __ } from '@wordpress/i18n';
import {
  RichText,
  useBlockProps,
  useInnerBlocksProps,
} from '@wordpress/block-editor';

import Inspector from './InspectorControls';
import MedalShape from './medal-shape';
import { getWrapperClasses, getWrapperStyles } from './styles';
import './style.scss';

/**
 * Medal — a figure between two shapes, with a caption under it.
 *
 * The shapes and the caption belong to this block: the shapes are chrome, and
 * the caption is a fixed part of the composition that always sits below and
 * always reads as one line. What goes *between* the shapes is inner blocks,
 * because it genuinely varies — a number on one medal, a row of avatars or a
 * rating on the next. Same split as Info Box.
 */

/**
 * Inserted with a figure already in place. An empty medal is two shapes around
 * nothing, and neither the size nor the gap control has anything to act on.
 * Mirrors block.json's `example`.
 *
 * Counter rather than a paragraph: the figure in a medal is almost always a
 * count, and Counter brings the tally animation and its own number typography
 * with it. It is swappable like any inner block — the template is a starting
 * point, not a cage.
 *
 * Counter can also carry a title, a description and an icon, and its default
 * variation fills all three in. A template creates the block directly and so
 * does not pick that variation up, but they are set empty explicitly: a medal
 * already has a caption beneath it, and a second label inside the wreath would
 * say the same thing twice.
 */
const TEMPLATE = [
  [
    'brandy/counter',
    {
      endValue: 50,
      title: '',
      description: '',
      iconName: '',
    },
  ],
];

export default function Edit({ attributes, setAttributes }) {
  const { shape, label } = attributes;

  const blockProps = useBlockProps({
    className: getWrapperClasses(attributes),
    style: getWrapperStyles(attributes),
  });

  // Unlocked: a rating row, a stack of avatars or an image are all reasonable
  // things to wreathe. The template is a starting point, not a cage.
  const innerBlocksProps = useInnerBlocksProps(
    { className: 'brandy-medal__content' },
    {
      template: TEMPLATE,
      templateLock: false,
    }
  );

  const sideShape = (side) =>
    shape &&
    shape !== 'none' && (
      <span className={`brandy-medal__shape brandy-medal__shape--${side}`}>
        <MedalShape shape={shape} />
      </span>
    );

  return (
    <>
      <Inspector attributes={attributes} setAttributes={setAttributes} />

      <div {...blockProps}>
        <div className="brandy-medal__body">
          {sideShape('start')}
          <div {...innerBlocksProps} />
          {sideShape('end')}
        </div>
        <RichText
          tagName="p"
          className="brandy-medal__label"
          value={label}
          onChange={(value) => setAttributes({ label: value })}
          placeholder={__('Add a caption…', 'brandy-blocks')}
          allowedFormats={[]}
        />
      </div>
    </>
  );
}
